import { useState } from 'react';
import {
  adjustCredits, getFamily, listFamilies, setUnlimited,
  type FamilyDetail,
} from '../api';
import { useAsync, message } from '../lib/async';
import { Empty, Id, Link, Refused, Result, when } from '../lib/ui';
import { go } from '../lib/route';

/**
 * Families, and the two controls that move money.
 *
 * WHAT WAS WRONG HERE. One `error` string served both the load and the
 * mutations, and it was rendered as an early return — so a server that
 * correctly refused a credit adjustment replaced the entire detail panel with a
 * load-failure box, taking the ledger, the videos, the commissions and the
 * reason the operator had just typed with it. The unlimited toggle had no error
 * handling at all: a bare async function on onClick, an unguarded await, so a
 * 403 after a role change or a 500 was an unhandled promise rejection and
 * NOTHING VISIBLE HAPPENED. The control that grants a household unlimited spend
 * looked like it had worked.
 *
 * Three states now, because they are three different facts: the load (Result),
 * a refused change (Refused, inline, panel intact), and busy (the button says
 * so and cannot be pressed twice).
 */
export function Families({ openId }: { openId?: string | undefined } = {}) {
  // Which family is open lives in the URL, not in state — that is what makes
  // "look at this one" a link rather than a set of instructions.
  const { result, reload } = useAsync(
    // `.rows`, not `.families`: every list endpoint now comes back through the
    // same `Listing` shape, so the array is always `rows` regardless of what the
    // handler names its key. /admin/families is one of the two lists with no
    // cursor — it is a capped scan — so there is nothing to page.
    () => listFamilies(),
    [],
  );

  return (
    <>
      <Result of={result} what="families" retry={reload}>
        {(page) =>
          !page.rows.length ? (
            <Empty what="families" />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Family</th><th>Credits</th><th>Created</th><th />
                </tr>
              </thead>
              <tbody>
                {page.rows.map((f) => (
                  <tr key={f.familyId}>
                    <td>
                      <code>{f.familyId.slice(0, 8)}</code>
                      {f.name && <span className="muted"> {f.name}</span>}
                    </td>
                    <td>
                      {f.unlimitedCredits
                        ? <span className="pill pill-on">unlimited</span>
                        : f.creditBalance ?? 0}
                    </td>
                    <td className="muted">{when(f.createdAt)}</td>
                    <td>
                      <button className="link"
                              onClick={() =>
                                go({ name: 'families',
                                     familyId: openId === f.familyId ? undefined : f.familyId })
                              }>
                        {openId === f.familyId ? 'close' : 'open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Result>

      {/* OUTSIDE the list's Result on purpose. /admin/families/<id> is a
          different call to /admin/families, and a deep link to one family has
          to render even when the capped scan behind the table failed or came
          back empty. */}
      {openId && <Detail key={openId} familyId={openId} onChanged={reload} />}
    </>
  );
}

function Detail({ familyId, onChanged }: { familyId: string; onChanged: () => void }) {
  const { result, reload } = useAsync(() => getFamily(familyId), [familyId]);

  return (
    <Result of={result} what="this family" retry={reload}>
      {(detail) => (
        <Body detail={detail} familyId={familyId} reload={reload} onChanged={onChanged} />
      )}
    </Result>
  );
}

/**
 * The panel, once the family has actually arrived.
 *
 * Split out so `detail` is a value and not a maybe: the controls below all act
 * on a family that is on screen, and a form that can render before its subject
 * exists is a form that can submit against the wrong one.
 */
function Body({
  detail,
  familyId,
  reload,
  onChanged,
}: {
  detail: FamilyDetail;
  familyId: string;
  reload: () => void;
  onChanged: () => void;
}) {
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'credits' | 'unlimited' | null>(null);
  /** A refused CHANGE. Never the load — that is `Result`'s, one level up. */
  const [refused, setRefused] = useState<{ what: string; error: string; cause: unknown } | null>(
    null,
  );

  /** Both controls run through here, so neither can be the one that forgets. */
  const change = async (what: 'credits' | 'unlimited', work: () => Promise<unknown>) => {
    setBusy(what);
    setRefused(null);
    try {
      await work();
      reload();
      onChanged();
      return true;
    } catch (err) {
      setRefused({
        what: what === 'credits' ? 'that credit adjustment' : 'that change to unlimited',
        error: message(err),
        cause: err,
      });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const grant = async (e: React.FormEvent) => {
    e.preventDefault();
    const applied = await change('credits', () =>
      adjustCredits(familyId, Number(credits), reason),
    );
    // Cleared only on success. They used to be cleared before the call could
    // fail, so a refusal cost the operator the reason they had typed.
    if (applied) {
      setCredits('');
      setReason('');
    }
  };

  const amount = Number(credits);
  // Non-zero is a SERVER rule ("credits must be non-zero"), mirrored here for
  // the same reason the mandatory reason is: `!credits` is false for the string
  // "0", so the button was enabled for the one value the server always refuses.
  const sendable = credits.trim() !== '' && Number.isFinite(amount) && amount !== 0;

  return (
    <section className="detail">
      <h2>
        <code>{familyId}</code>
      </h2>

      {/* Inline, and above both controls rather than instead of the panel. */}
      {refused && <Refused what={refused.what} error={refused.error} cause={refused.cause} />}

      <div className="row">
        <button className={detail.unlimitedCredits ? 'primary' : ''}
                disabled={busy !== null}
                onClick={() =>
                  void change('unlimited', () =>
                    setUnlimited(familyId, !detail.unlimitedCredits),
                  )
                }>
          {busy === 'unlimited'
            ? '…'
            : detail.unlimitedCredits
              ? 'Unlimited — click to revoke'
              : 'Make unlimited'}
        </button>
        <span className="muted">
          For internal and test accounts. Commissions still run and still cost
          us money at the provider; the balance simply is not charged, and every
          one is recorded in the ledger below.
        </span>
      </div>

      {/* A reason is mandatory server-side, so it is mandatory here too rather
          than letting the request fail after the fact. */}
      <form className="row" onSubmit={grant}>
        <input type="number" placeholder="credits (+/-)" value={credits}
               onChange={(e) => setCredits(e.target.value)} required />
        <input placeholder="reason (recorded in the ledger)" value={reason}
               onChange={(e) => setReason(e.target.value)} required />
        <button className="primary" disabled={busy !== null || !sendable || !reason}>
          {busy === 'credits' ? '…' : 'Apply'}
        </button>
      </form>

      {/* WHO IS IN THIS HOUSEHOLD, which this panel simply did not render.
          `/admin/families/<id>` has returned `members` since the byFamily index
          on showling-members got its first consumer, `FamilyDetail` has always
          declared the field, and the payload arrives on every load — the table
          was never written, so the array was fetched and dropped. Nothing else
          in the console lists members either (`listMembers` in api.ts has no
          caller), so "why can this person see my child's films" — the question
          the handler's own docstring says support has to be able to answer —
          was unanswerable from the portal.

          `members` stays optional here because the handler returns [] rather
          than failing when the membership index is unavailable, so "no members"
          is a real state a real family can be in and it must not read as a
          rendering bug. */}
      <h3>Members</h3>
      {!detail.members?.length ? (
        <>
          <Empty what="members" />
          <p className="muted small">
            Every household has at least one, so an empty list here is either a
            family created before the members table existed or the membership
            index being unavailable — the handler returns an empty array rather
            than failing the whole page, so the credit controls above still work.
          </p>
        </>
      ) : (
        <table>
          <thead><tr><th>Principal</th><th>Role</th><th>Joined via</th><th>When</th></tr></thead>
          <tbody>
            {detail.members.map((m) => (
              <tr key={m.principalId}>
                {/* The Entra object id, in full and copyable. It is what an
                    operator pastes into the tenant to put a name to a person —
                    showling-members deliberately stores no email. */}
                <td><Id value={m.principalId} chars={36} /></td>
                <td>{m.role ?? <span className="muted">—</span>}</td>
                <td className="muted small">{m.joinedVia}</td>
                <td className="muted">{when(m.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Ledger</h3>
      {!detail.ledger.length ? <Empty what="entries" /> : (
        <table>
          <thead><tr><th>When</th><th>Type</th><th>Credits</th><th>Why</th></tr></thead>
          <tbody>
            {detail.ledger.map((e) => (
              <tr key={e.entryId}>
                <td className="muted">{when(e.at)}</td>
                <td>{e.type}</td>
                <td className={Number(e.credits) < 0 ? 'neg' : ''}>
                  {e.credits}
                  {e.waived && (
                    <span className="muted"> (waived {e.wouldHaveCost})</span>
                  )}
                </td>
                <td className="muted">
                  {e.reason}
                  {e.actor && <> · by {e.actor}</>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Videos</h3>
      {!detail.videos.length ? <Empty what="videos" /> : (
        <table>
          <thead><tr><th>Title</th><th>Status</th><th>Reuse</th><th>When</th></tr></thead>
          <tbody>
            {detail.videos.map((v) => (
              <tr key={v.videoId}>
                <td>{v.title}</td>
                <td>
                  {v.status}
                  {v.stitched && <span className="pill"> stitched</span>}
                </td>
                <td className="muted">
                  {v.reuse?.segments
                    ? `${v.reuse.reused ?? 0}/${v.reuse.segments} reused`
                    : ''}
                </td>
                <td className="muted">{when(v.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Commissions</h3>
      {!detail.commissions.length ? <Empty what="commissions" /> : (
        <table>
          <thead><tr><th>Request</th><th>Status</th><th>When</th></tr></thead>
          <tbody>
            {detail.commissions.map((c) => (
              <tr key={c.commissionId}>
                {/* A dead end until now: this is the one place in the console
                    that lists a household's films, and the row went nowhere —
                    the shot plan, the failure cause, the generated clips and
                    the money are all one route away at /commissions/<id>, and
                    an operator had to go via Pipeline and find the row again. */}
                <td>
                  <Link to={{ name: 'commission', commissionId: c.commissionId }}>
                    {c.request || c.commissionId}
                  </Link>
                </td>
                <td>
                  {c.status}
                  {c.failureReason && (
                    <div className="muted small">{c.failureReason}</div>
                  )}
                </td>
                <td className="muted">{when(c.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
